/**
 * Le contrat de la **surveillance des transactions LBC/FT**, côté navigateur
 * (epic 11, `EX-AML-004` à `EX-AML-011`, `EX-BO-004`).
 *
 * **Ce fichier ne décide de rien.** Il transporte, et il déclare ce que le
 * serveur rend — les types suivent les `record` de `SurveillanceApi.java`, sans
 * rien y ajouter. `compliance-maquette.test.tsx` **relit le fichier Java** et
 * confronte champ par champ : recopier un contrat, c'est en faire une seconde
 * source de vérité qui vieillira (`D-35`, `D-45`).
 *
 * ## Sept routes servies, aucun écran — jusqu'ici
 *
 * `tools/ci/test_routes_appelees_et_servies.py` les nommait toutes les sept :
 * la file, l'investigation, le reporting, les règles, les rejeux, le dossier de
 * soupçon et son export. C'est la **dixième** occurrence de la famille de
 * `D-25`, `D-30`, `D-35`, `D-45` et `D-57` — un métier livré, éprouvé, vert, et
 * que rien ne raccorde. Les sept sont appelées par `ComplianceScreen`.
 *
 * ## Il n'y a ici que des lectures, et c'est le serveur qui l'a voulu
 *
 * `SurveillanceApi` l'écrit en tête : créer une règle, la modifier, rejouer un
 * scénario, traiter une alerte, geler un compte et constituer un dossier de
 * soupçon passent tous par `POST /api/backoffice/actions/{action}`, la route
 * unique du socle — `EX-BO-002` fait porter l'habilitation sur l'**action** et
 * non sur l'écran. Aucune de ces écritures n'est câblée par ce chantier ; voir
 * l'en-tête de `ComplianceScreen.tsx`, qui dit laquelle et pourquoi.
 *
 * ## Le motif est obligatoire sur les sept, et ce n'est pas une formalité
 *
 * `BackOfficeService.requireReason` refuse un motif vide, et chaque lecture
 * écrit une entrée au journal d'audit (`EX-BO-004`, `EX-DAT-007`). Les motifs
 * sont des **constantes** — l'analyste ouvre la file toujours pour la même
 * raison, la consulter — comme sur le listage du journal d'audit et les quatre
 * lectures du rapprochement. Ce qui distingue une consultation d'une autre est
 * déjà journalisé par le serveur : la file inscrit le nombre d'alertes révélées
 * et les quatre filtres, l'investigation inscrit **le nombre de contreparties
 * révélées**, parce que le graphe expose des tiers qui n'ont rien demandé.
 *
 * **Ce que la file révèle, et pourquoi son entrée d'audit suffit.** La file rend
 * des `customerId` — des identifiants techniques —, jamais un nom, jamais un
 * numéro. C'est exactement l'état où était la file de revue KYC **avant** que
 * son entrée ne passe de `KYC_QUEUE` à `KYC_QUEUE_IDENTITIES` : une lecture qui
 * révélait deux cents états civils écrivait la même ligne qu'une lecture qui ne
 * révélait que des identifiants. La conséquence pour l'écran est écrite dans
 * `ComplianceScreen.tsx` et gardée par un cas : **la file ne résout aucune
 * identité**. Le jour où elle appellerait `GET /customers/{id}` par ligne pour
 * afficher des noms, la trace `AML_ALERT_QUEUE` deviendrait fausse sans que
 * personne la corrige.
 */

import type { ApiClient } from './api-client.js';

/** Le chemin de la file d'alertes, tel que `SurveillanceApi` le sert. */
export const alertQueuePath = '/api/backoffice/aml/alerts';

/** Le chemin des règles de surveillance et de leurs seuils. */
export const monitoringRulesPath = '/api/backoffice/aml/rules';

/** Le chemin du reporting de conformité. */
export const monitoringReportPath = '/api/backoffice/aml/report';

/**
 * Le chemin d'un dossier de déclaration de soupçon.
 *
 * Écrit en fonction, et non en constante de préfixe : `/aml/suspicions` tout
 * court **n'est pas une route servie** — le serveur ne liste pas les dossiers,
 * il en rend un désigné. Une constante qui porterait le préfixe nu ferait dire
 * à `tools/ci/test_routes_appelees_et_servies.py` qu'un écran appelle un chemin
 * que personne ne sert, et c'est exactement le défaut que ce contrôle existe
 * pour attraper.
 */
export function suspicionFilePath(fileId: string): string {
  return `/api/backoffice/aml/suspicions/${encodeURIComponent(fileId)}`;
}

/** Le chemin de l'export d'un dossier, écrit en clair pour la même raison. */
export function suspicionExportPath(fileId: string): string {
  return `/api/backoffice/aml/suspicions/${encodeURIComponent(fileId)}/export`;
}

/** Le motif d'audit de la lecture de la file. */
export const alertQueueReason = 'consultation-de-la-file-d-alertes';

/** Le motif d'audit de l'investigation ouverte sur une alerte. */
export const investigationReason = 'investigation-d-une-alerte-de-surveillance';

/** Le motif d'audit de la lecture des règles et de leurs seuils. */
export const monitoringRulesReason = 'consultation-des-regles-de-surveillance';

/** Le motif d'audit de la lecture des rejeux d'une règle. */
export const replayReason = 'consultation-des-rejeux-d-une-regle';

/** Le motif d'audit de la lecture du reporting. */
export const monitoringReportReason = 'consultation-du-reporting-de-conformite';

/** Le motif d'audit de la lecture d'un dossier de soupçon. */
export const suspicionFileReason = 'consultation-d-un-dossier-de-soupcon';

/**
 * Le motif d'audit de l'**export** d'un dossier de soupçon.
 *
 * Distinct de la consultation, et il faut qu'il le soit : le serveur passe par
 * la même lecture habilitée — « exporter est une consultation de plus, pas une
 * porte de service » —, si bien que seul le motif distingue, au journal, le
 * dossier qu'on a lu à l'écran de celui qui est sorti du produit en fichier.
 */
export const suspicionExportReason = 'export-d-un-dossier-de-soupcon';

/** Une règle de surveillance et ses seuils, tels que le back-office les règle. */
export interface MonitoringRule {
  readonly id: string;
  readonly code: string;
  readonly scenarioCode: string;
  readonly label: string;
  readonly perimeter: string;
  readonly severity: number;
  readonly action: string;
  /**
   * `ACTIVE` ou `SIMULATION` — et la distinction n'est pas cosmétique
   * (`EX-AML-010`) : une règle en simulation **enregistre les alertes qu'elle
   * aurait produites sans alimenter la file**. Une alerte lue dans cette file
   * vient donc toujours d'une règle active ; le panneau montre le mode pour
   * qu'un analyste qui cherche pourquoi un scénario ne lève rien le voie.
   */
  readonly mode: string;
  readonly version: number;
  /** Les seuils, tels que la base les porte. Aucun n'est écrit dans le code. */
  readonly parameters: Readonly<Record<string, string>>;
}

/**
 * Une alerte de la file.
 *
 * `customerId` est un **identifiant**, pas un nom ni un numéro : la maquette
 * écrit « 77 645 12 08 » dans sa colonne « Compte », et cette route ne sert ni
 * `msisdn` ni état civil. Voir l'en-tête de ce fichier — les résoudre ligne à
 * ligne ferait mentir l'entrée d'audit de la file.
 *
 * `observed` porte ce que le scénario a **constaté**, avec ses propres clés —
 * `montant`, `seuil.amount_minor`, `operations.serrees`… Elles sont
 * scénario-dépendantes, en français, et le serveur ne les traduit pas : l'écran
 * les rend telles quelles plutôt que d'en élire une par scénario, ce qui serait
 * la liste blanche partagée de `G3` — le premier scénario ajouté la rendrait
 * fausse en silence.
 */
export interface MonitoringAlert {
  readonly id: string;
  readonly scenarioCode: string;
  readonly scenarioLabel: string;
  readonly ruleCode: string;
  readonly ruleVersion: number;
  readonly customerId: string;
  readonly transactionId: string | null;
  readonly severity: number;
  readonly status: string;
  readonly assignedTo: string | null;
  readonly decision: string | null;
  readonly decisionReason: string | null;
  /** Le délai de traitement, **mesuré et figé à la décision** par le serveur. */
  readonly handlingSeconds: number | null;
  readonly observed: Readonly<Record<string, string>>;
  readonly occurredAt: string;
  readonly raisedAt: string;
}

/** Une opération de l'historique d'investigation. */
export interface Observation {
  readonly transactionId: string | null;
  readonly direction: string;
  readonly amount: number;
  readonly channel: string;
  readonly txType: string;
  readonly counterpartyId: string | null;
  readonly occurredAt: string;
}

/** Une arête du graphe : qui envoie à qui, avec montants et fréquences. */
export interface CounterpartyEdge {
  readonly from: string;
  readonly to: string;
  readonly operations: number;
  readonly totalAmount: number;
  readonly averageAmount: number;
  readonly firstSeen: string;
  readonly lastSeen: string;
}

/** Le graphe des contreparties, et la fenêtre sur laquelle il est bâti. */
export interface CounterpartyGraph {
  readonly centre: string;
  readonly from: string;
  readonly to: string;
  readonly edges: readonly CounterpartyEdge[];
}

/** Une ligne de l'historique d'une alerte. */
export interface AlertTimelineEvent {
  readonly kind: string;
  readonly detail: string;
  /**
   * L'acteur, **ou les quatre lettres `null`**.
   *
   * Le serveur écrit `String.valueOf(evenement.actorId())` : une ligne sans
   * acteur — une alerte levée par le moteur, personne derrière — arrive donc
   * comme la chaîne `"null"`. L'écran la reconnaît et dit « le dispositif »
   * plutôt que d'afficher le mot à un analyste qui le lirait comme une panne.
   */
  readonly actorId: string;
  readonly at: string;
}

/** Une pièce jointe, décrite sans son contenu. */
export interface AlertAttachment {
  readonly id: string;
  readonly filename: string;
  readonly mediaType: string;
  readonly byteSize: number;
  readonly digest: string;
  readonly addedAt: string;
}

/**
 * L'investigation « en un clic », en **une seule** réponse (`EX-AML-005`).
 *
 * Cinq appels auraient donné cinq entrées d'audit pour un seul regard ; c'est
 * le serveur qui l'a tranché, et l'écran n'a pas à le défaire.
 */
export interface Investigation {
  readonly alert: MonitoringAlert;
  readonly customerPublicRef: string | null;
  readonly kycCaseId: string | null;
  readonly balanceMinorUnits: number | null;
  readonly history: readonly Observation[];
  readonly graph: CounterpartyGraph;
  readonly priorAlerts: readonly MonitoringAlert[];
  readonly timeline: readonly AlertTimelineEvent[];
  readonly attachments: readonly AlertAttachment[];
  readonly suspicionFileId: string | null;
}

/** Un rejeu sur historique, et son compte (`EX-AML-010`). */
export interface ReplayRun {
  readonly id: string;
  readonly ruleVersion: number;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly observationsScanned: number;
  /** Le nombre d'alertes que la règle **aurait** produites. Jamais levées. */
  readonly wouldHaveRaised: number;
  readonly parameters: Readonly<Record<string, string>>;
  readonly finishedAt: string;
}

/** Une ligne du reporting, par scénario (`EX-AML-009`). */
export interface ReportLine {
  readonly scenarioCode: string;
  readonly scenarioLabel: string;
  readonly raised: number;
  readonly handled: number;
  readonly falsePositives: number;
  readonly falsePositivePercent: number;
  readonly averageHandlingSeconds: number;
  readonly recalibrationCandidate: boolean;
}

/** Le reporting de conformité, sur la fenêtre que le serveur a appliquée. */
export interface MonitoringReport {
  readonly from: string;
  readonly to: string;
  readonly totalAlerts: number;
  readonly suspicionFiles: number;
  readonly frozenAccounts: number;
  readonly lines: readonly ReportLine[];
}

/** Une pièce d'un dossier de soupçon. */
export interface SuspicionItem {
  readonly position: number;
  readonly kind: string;
  readonly reference: string;
  readonly label: string;
}

/**
 * Un dossier de déclaration de soupçon (`EX-AML-008`).
 *
 * **Aucun champ de notification, et il ne peut pas y en avoir** : le serveur
 * n'en sert aucun, et l'interdiction d'informer le client est absolue — elle
 * prime sur toute règle de notification, y compris la notification écrite
 * inconditionnelle d'`EX-NOT-001`.
 */
export interface SuspicionFile {
  readonly id: string;
  readonly reference: string;
  readonly alertId: string;
  readonly status: string;
  readonly summary: string;
  readonly narrative: string;
  readonly openedAt: string;
  readonly submittedAt: string | null;
  readonly authorityReference: string | null;
  readonly items: readonly SuspicionItem[];
}

/** Les quatre filtres que la route de la file accepte. Aucun autre n'existe. */
export interface AlertQueueFilter {
  readonly status?: string;
  readonly scenario?: string;
  readonly assignedTo?: string;
  readonly minimumSeverity?: string;
}

/**
 * Lit la file d'alertes, priorisée par gravité côté serveur.
 *
 * Les paramètres vides ne sont **pas** envoyés : la route traite le blanc comme
 * l'absence de filtre, mais une URL qui porterait `scenario=` dirait au journal
 * d'audit qu'un filtre a été posé alors qu'aucun ne l'a été.
 */
export async function fetchAlertQueue(
  client: ApiClient,
  filtre: AlertQueueFilter = {},
): Promise<readonly MonitoringAlert[]> {
  const parametres = new URLSearchParams({ reason: alertQueueReason });
  for (const [nom, valeur] of Object.entries(filtre)) {
    if (valeur !== undefined && valeur !== '') parametres.set(nom, valeur);
  }
  return client.request<readonly MonitoringAlert[]>(`${alertQueuePath}?${parametres.toString()}`);
}

/** Ouvre l'investigation d'une alerte. C'est l'appel que le journal nomme. */
export async function fetchInvestigation(
  client: ApiClient,
  alertId: string,
): Promise<Investigation> {
  return client.request<Investigation>(
    `${alertQueuePath}/${encodeURIComponent(alertId)}` +
      `?reason=${encodeURIComponent(investigationReason)}`,
  );
}

/** Lit les règles de surveillance et leurs seuils. */
export async function fetchMonitoringRules(
  client: ApiClient,
): Promise<readonly MonitoringRule[]> {
  return client.request<readonly MonitoringRule[]>(
    `${monitoringRulesPath}?reason=${encodeURIComponent(monitoringRulesReason)}`,
  );
}

/** Lit les rejeux d'une règle : combien d'alertes elle **aurait** produites. */
export async function fetchReplayRuns(
  client: ApiClient,
  ruleCode: string,
): Promise<readonly ReplayRun[]> {
  return client.request<readonly ReplayRun[]>(
    `${monitoringRulesPath}/${encodeURIComponent(ruleCode)}/replays` +
      `?reason=${encodeURIComponent(replayReason)}`,
  );
}

/** Lit le reporting de conformité. Sans bornes, le serveur applique sa fenêtre. */
export async function fetchMonitoringReport(client: ApiClient): Promise<MonitoringReport> {
  return client.request<MonitoringReport>(
    `${monitoringReportPath}?reason=${encodeURIComponent(monitoringReportReason)}`,
  );
}

/** Lit un dossier de déclaration de soupçon. */
export async function fetchSuspicionFile(
  client: ApiClient,
  fileId: string,
): Promise<SuspicionFile> {
  return client.request<SuspicionFile>(
    `${suspicionFilePath(fileId)}?reason=${encodeURIComponent(suspicionFileReason)}`,
  );
}

/**
 * Lit l'**export** d'un dossier, en texte délimité.
 *
 * `requestBytes` et non `request` : le serveur rend `text/plain`, et la lecture
 * JSON du client lèverait sur le premier caractère. Le transport est le même —
 * jeton, identifiant de corrélation, borne de délai —, et c'est ce qui permet
 * au serveur d'habiliter l'export et de l'inscrire au journal au nom de
 * quelqu'un.
 */
export async function fetchSuspicionExport(
  client: ApiClient,
  fileId: string,
): Promise<string> {
  const octets = await client.requestBytes(
    `${suspicionExportPath(fileId)}?reason=${encodeURIComponent(suspicionExportReason)}`,
  );
  return octets.blob.text();
}
